/**
 * The local replica, in IndexedDB via Dexie (data model §6, ADR-0004).
 *
 * The client holds the whole manifest, which is what lets search, filtering and
 * every count run locally with no server round trip (assumption A7). Dexie is the
 * durable copy; `replica.ts` keeps an in-memory `Manifest` as the working one and
 * folds this back into it at startup.
 *
 * **The `outbox` is the only data in this system that exists in exactly one
 * place.** Everything else is recoverable from the server's log. That is risk R4,
 * and it is why the write path puts the projection update and the outbox append in
 * a single transaction: a crash between them is then not representable.
 *
 * Children hang off their own stores rather than being nested in the item row,
 * matching §6, so the compound indexes that Phase 3's filters need have somewhere
 * to live.
 */
import Dexie, { type Table } from 'dexie';
import type {
  Cell,
  EntityRecord,
  IntentionRecord,
  ItemRecord,
  PhotoRecord,
  TagRecord,
  Tombstoned,
} from '@intentionalist/shared';

/**
 * Item-level columns.
 *
 * `deletedHlc`, `categoryId`, `brand`, `color`, `currentIntention` and
 * the indexed columns are flattened out of the record because IndexedDB cannot index
 * inside a nested object, and §6's compound indexes are specified over exactly
 * these. They are derived on write and never read back as truth -- `record` is.
 */
export interface ItemRow {
  itemId: string;
  record: SerializedItem;
  /** Empty string rather than null: IndexedDB will not index null. */
  deletedHlc: string;
  categoryId: string;
  brand: string;
  color: string;
  currentIntention: string;
}

/** The item record with its `Map`s as plain objects, which IndexedDB can store. */
export interface SerializedItem {
  itemId: string;
  createdHlc: string | null;
  fields: ItemRecord['fields'];
  customFields: Record<string, Cell<unknown>>;
  lifecycle: Tombstoned | null;
  /**
   * Release is absent here on purpose: it has its own store, as it has its own
   * `REL#` row on the server. Two homes for one fact is how they come to disagree.
   */
  photoOrder: Cell<readonly string[]> | null;
  primaryPhotoId: Cell<string> | null;
  currentIntention: ItemRecord['currentIntention'];
  reviewAgainOn: string | null;
}

/** At most one per item, and its existence is what archives it (FR-7.4). */
export interface ReleaseRow {
  itemId: string;
  release: ItemRecord['release'];
  decision: Tombstoned | null;
}

export interface IntentionRow extends IntentionRecord {
  itemId: string;
}
export interface ItemTagRow extends Tombstoned {
  itemId: string;
  tagId: string;
}
export interface PhotoRow extends Omit<PhotoRecord, 'metadata' | 'uploaded' | 'lifecycle'> {
  itemId: string;
  metadata: PhotoRecord['metadata'];
  uploaded: PhotoRecord['uploaded'];
  lifecycle: PhotoRecord['lifecycle'];
}
export interface TagRow {
  tagId: string;
  record: Omit<TagRecord, 'tagId'>;
}
export interface EntityRow {
  entityId: string;
  fields: Record<string, Cell<unknown>>;
  lifecycle: Tombstoned | null;
  links: Record<string, Tombstoned>;
}

/** One event, kept locally for history and for re-folding the projection (§6). */
export interface EventRow {
  /** `<hlc>#<eventId>`, so the store's natural order is causal order. */
  key: string;
  hlc: string;
  eventId: string;
  event: unknown;
}

/**
 * An event not yet accepted by the server.
 *
 * Cleared only on `accepted`, `duplicates` or `rejected` (sync protocol §4, plus
 * the `rejected` addition in contracts.ts). Anything else and it is retried, which
 * is safe because the server deduplicates on `eventId`.
 */
export interface OutboxRow {
  eventId: string;
  hlc: string;
  event: unknown;
  /** For backoff and for the pending count FR-10.4 wants shown. */
  queuedAt: number;
  attempts: number;
}

export interface MetaRow {
  key: string;
  value: unknown;
}

export class ReplicaDb extends Dexie {
  items!: Table<ItemRow, string>;
  intentions!: Table<IntentionRow, [string, string]>;
  itemTags!: Table<ItemTagRow, [string, string]>;
  photos!: Table<PhotoRow, [string, string]>;
  releases!: Table<ReleaseRow, string>;
  tags!: Table<TagRow, string>;
  categories!: Table<EntityRow, string>;
  locations!: Table<EntityRow, string>;
  customFields!: Table<EntityRow, string>;
  investments!: Table<EntityRow, string>;
  eventLog!: Table<EventRow, string>;
  outbox!: Table<OutboxRow, string>;
  photoBlobs!: Table<{ key: string; blob: Blob }, string>;
  meta!: Table<MetaRow, string>;

  constructor(name = 'intentionalist') {
    super(name);
    /*
     * Version 1 is declared as it originally shipped, `colour` included.
     *
     * A declared schema is not a comment: Dexie compares it against what the
     * database on disk says, so editing version 1 in place would make an
     * existing device disagree with its own store. Renaming the index to
     * `color` here looked like part of a spelling sweep and would have been a
     * silent data loss -- the rows on disk carry `colour`, so hydration would
     * have read `undefined` and the field would have vanished from every item
     * on that device until something replayed the log.
     */
    this.version(1).stores({
      // §6's compound indexes. `deletedHlc` leads both so that the common case --
      // "not deleted, filtered by something" -- is one index range rather than a
      // scan with a predicate.
      items:
        'itemId, deletedHlc, [deletedHlc+categoryId], [deletedHlc+currentIntention], lastUsedOn, brand, colour',
      intentions: '[itemId+hlc], itemId',
      // Both directions: an item's tags, and a tag's items.
      itemTags: '[itemId+tagId], [tagId+itemId]',
      photos: '[itemId+photoId], itemId',
      releases: 'itemId',
      tags: 'tagId',
      categories: 'entityId',
      locations: 'entityId',
      customFields: 'entityId',
      investments: 'entityId',
      eventLog: 'key, hlc',
      outbox: 'eventId, hlc, queuedAt',
      photoBlobs: 'key',
      meta: 'key',
    });

    /*
     * Version 2 renames the `colour` index and property to `color`.
     *
     * The index is re-declared, which Dexie rebuilds on upgrade. The *property*
     * is the part that needs the `upgrade` callback: an index points at a
     * property name, so declaring `color` over rows that carry `colour` would
     * produce an empty index and a lost field.
     *
     * The tag table needs no such pass. Nothing ever wrote a tag colour --
     * FR-4.20 is unbuilt -- so there is nothing on disk to move.
     */
    this.version(2)
      .stores({
        items:
          'itemId, deletedHlc, [deletedHlc+categoryId], [deletedHlc+currentIntention], lastUsedOn, brand, color',
      })
      .upgrade((tx) =>
        tx
          .table<ItemRow & { colour?: string }>('items')
          .toCollection()
          .modify((row) => {
            if (row.colour === undefined) return;
            row.color = row.colour;
            delete row.colour;
          }),
      );

    /*
     * Version 3 drops recorded uses.
     *
     * The table goes (`uses: null`) and the `lastUsedOn` index with it, because
     * nothing derives either any more. The rows are not migrated anywhere: a use
     * was only ever a projection of `use.recorded` events, and those are still
     * in the log on the server and in `eventLog` here. Nothing is being thrown
     * away that could not be replayed if this decision were reversed.
     *
     * The stale `lastUsedOn` property left on existing item rows is harmless --
     * the projection no longer writes it and nothing reads it -- and is cleared
     * on the next write of each row rather than in a pass over the table, which
     * would be a lot of IO for a field nobody looks at.
     */
    this.version(3).stores({
      items:
        'itemId, deletedHlc, [deletedHlc+categoryId], [deletedHlc+currentIntention], brand, color',
      uses: null,
    });
  }
}

/**
 * Whose manifest this database holds.
 *
 * IndexedDB is scoped to the origin, not to the account, so a second person
 * signing in on the same browser would otherwise inherit the first one's catalog
 * -- a privacy failure, and one that would then be pushed back up under the wrong
 * user id. `Replica.claim` compares this and wipes when it differs.
 */
export const META_USER = 'userId';

/**
 * A review session in progress (FR-5.10).
 *
 * In `meta` rather than component state so that pausing is the same action as
 * closing the tab: a session survives a reload, a crash, and the app being
 * killed on a phone, none of which should cost someone the decisions they have
 * already made.
 */
export const META_REVIEW_SESSION = 'reviewSession';

export const META_CURSOR = 'pullCursor';
export const META_LAST_SYNC = 'lastSyncAt';
export const META_SYNC_ERROR = 'syncError';

// ---------------------------------------------------------------------------
// Codecs
// ---------------------------------------------------------------------------

const toObject = <V>(map: Map<string, V>): Record<string, V> => Object.fromEntries(map);
const toMap = <V>(value: Record<string, V> | undefined): Map<string, V> =>
  new Map(Object.entries(value ?? {}));

/** Index columns use '' for absent, because IndexedDB skips records with a null key part. */
const indexable = (value: unknown): string => (typeof value === 'string' ? value : '');

export function itemToRow(item: ItemRecord): ItemRow {
  const field = (name: 'categoryId' | 'brand' | 'color'): string =>
    indexable((item.fields as Record<string, Cell<unknown> | undefined>)[name]?.v);

  return {
    itemId: item.itemId,
    record: {
      itemId: item.itemId,
      createdHlc: item.createdHlc,
      fields: item.fields,
      customFields: toObject(item.customFields),
      lifecycle: item.lifecycle,
      photoOrder: item.photoOrder,
      primaryPhotoId: item.primaryPhotoId,
      currentIntention: item.currentIntention,
      reviewAgainOn: item.reviewAgainOn,
    },
    deletedHlc: item.lifecycle?.present === false ? item.lifecycle.hlc : '',
    categoryId: field('categoryId'),
    brand: field('brand'),
    color: field('color'),
    currentIntention: indexable(item.currentIntention),
  };
}

/** Children are supplied separately, because they come from their own stores. */
export function rowToItem(
  row: ItemRow,
  children: {
    intentions?: IntentionRow[];
    itemTags?: ItemTagRow[];
    photos?: PhotoRow[];
    release?: ReleaseRow | undefined;
  } = {},
): ItemRecord {
  const r = row.record;
  return {
    itemId: r.itemId,
    createdHlc: r.createdHlc,
    fields: r.fields,
    customFields: toMap(r.customFields),
    lifecycle: r.lifecycle,
    release: children.release?.release ?? null,
    releaseDecision: children.release?.decision ?? null,
    intentions: new Map((children.intentions ?? []).map((i) => [i.hlc, stripItemId(i)])),
    tags: new Map(
      (children.itemTags ?? []).map((t) => [t.tagId, { present: t.present, hlc: t.hlc }]),
    ),
    photos: new Map(
      (children.photos ?? []).map((p) => [
        p.photoId,
        {
          photoId: p.photoId,
          metadata: p.metadata,
          uploaded: p.uploaded,
          lifecycle: p.lifecycle,
        },
      ]),
    ),
    photoOrder: r.photoOrder,
    primaryPhotoId: r.primaryPhotoId,
    currentIntention: r.currentIntention,
    reviewAgainOn: r.reviewAgainOn,
  };
}

/** The child records carry their parent only as a key; the record itself does not. */
function stripItemId<T extends { itemId: string }>(row: T): Omit<T, 'itemId'> {
  const rest: Record<string, unknown> = { ...row };
  delete rest['itemId'];
  return rest as Omit<T, 'itemId'>;
}

export const releaseToRow = (item: ItemRecord): ReleaseRow => ({
  itemId: item.itemId,
  release: item.release,
  decision: item.releaseDecision,
});

export const tagToRow = (tag: TagRecord): TagRow => ({
  tagId: tag.tagId,
  record: {
    displayName: tag.displayName,
    normalizedName: tag.normalizedName,
    color: tag.color,
    lifecycle: tag.lifecycle,
    mergedInto: tag.mergedInto,
  },
});

export const rowToTag = (row: TagRow): TagRecord => ({ tagId: row.tagId, ...row.record });

export const entityToRow = (record: EntityRecord): EntityRow => ({
  entityId: record.entityId,
  fields: toObject(record.fields),
  lifecycle: record.lifecycle,
  links: toObject(record.links),
});

export const rowToEntity = (row: EntityRow): EntityRecord => ({
  entityId: row.entityId,
  fields: toMap(row.fields),
  lifecycle: row.lifecycle,
  links: toMap(row.links),
});
